import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Search, ChevronLeft } from 'lucide-react';

import { Input } from '@/components/forms';
import { Button, Spinner } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/stores/useAuthStore';
import { sanitizeEmail } from '@/lib/sanitize';
import { logger } from '@/lib/logger';
import type { ResetPasswordRequest } from '@/types/auth';

import styles from './ForgotPassword.module.css';

export function ForgotPassword() {
  const navigate = useNavigate();
  const { requestPasswordReset, isLoading, isLogged } = useAuthStore();

  const [formData, setFormData] = useState<ResetPasswordRequest>({
    email: '',
  });

  useEffect(() => {
    if (isLogged) {
      navigate(ROUTES.dashboard);
    }
  }, [isLogged, navigate]);

  const handleFormDataChange = (key: string, value: string) => {
    setFormData((prevState: ResetPasswordRequest) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateForm = (formData: ResetPasswordRequest) => {
    const { email } = formData;

    if (!email) {
      return { isValid: false, message: 'Please fill in all fields' };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    return { isValid: true, message: '' };
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validation = validateForm(formData);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    const sanitizedData: ResetPasswordRequest = {
      email: sanitizeEmail(formData.email),
    };

    try {
      const status: boolean = await requestPasswordReset(sanitizedData);
      if (status) {
        navigate(ROUTES.login);
        return;
      }
    } catch (error) {
      toast.error('An error occurred while requesting password reset');
      logger.error('Password reset request error:', error);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.backRow}>
        <button
          className={styles.backButton}
          onClick={() => navigate(ROUTES.login)}
          aria-label="Back to login"
        >
          <ChevronLeft size={16} /> Back to Login
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.logoRow}>
          <div className={styles.logoIcon}>
            <Search size={16} />
          </div>
          <span className={styles.logoText}>Leads Manager</span>
        </div>

        <h1 className={styles.title}>Reset Password</h1>
        <p className={styles.description}>
          Enter your email and we'll send you a link to reset your password.
        </p>

        <form className={styles.form} onSubmit={onSubmit}>
          <div className={styles.field}>
            <label className={styles.label}>Email</label>
            <Input
              name="email"
              type="email"
              value={formData.email}
              setValue={(value) => handleFormDataChange('email', value)}
              placeholder="you@example.com"
              variant="bordered"
            />
          </div>

          <Button variant="primary" size="md" type="submit" disabled={isLoading}>
            {isLoading ? <Spinner variant="secondary" size="sm" /> : 'Send Reset Link'}
          </Button>
        </form>

        <div className={styles.footer}>
          <span>Remember your password?</span>
          <button className={styles.footerLink} onClick={() => navigate(ROUTES.login)}>
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
